import React, { useEffect, useState } from 'react';
import { Row } from 'reactstrap';
import { PageTitle } from '../../../components/PageTitle';
import { EmailTemplateService } from '../../../services/EmailTemplateService';
import { EmailTemplate } from '../../../entities/EmailTemplate';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { Body } from './Body';
import { UpsertEmailTemplateDto } from './UpsertEmailTemplateDto';
import { useFieldArray, useForm } from 'react-hook-form';
import { FormErrorMessage } from '../../../components/FormErrorMessage';
import { Card } from '../../../components/Card';
import { AttachmentDto } from '../../../services/dtos/EmailDto';
import { FileContent, useImperativeFilePicker } from 'use-file-picker';
import { ApiError } from '../../../services/ApiService/errors/ApiError';
import { getRedirectUriQueryParam } from '../../../utils/url';
import { FilesSizeValidator } from '../../../utils/useFilePicker/filesSizeValidator';
import { FilesLimitValidator } from '../../../utils/useFilePicker/filesLimitValidator';
import {
  emailTemplateToDto,
  MAX_FILE_SIZE_MB,
  MAX_FILES,
  MAX_FILES_SIZE_MB,
} from '../../../components/EmailTemplateEditor';

const REDIRECT_URI =
  getRedirectUriQueryParam() ?? '/organization_settings/email_templates';

interface PropTypes {
  edit: boolean;
  emailTemplate: EmailTemplate;
}

function Title(props: { edit: boolean; name?: string }) {
  if (props.edit) {
    return (
      <PageTitle
        text={
          <>
            <span>Edit / </span>
            <span className='text-dark-200'>{props.name}</span>
          </>
        }
      />
    );
  } else {
    return <PageTitle text={'Add Email Template'} />;
  }
}

export default function SetupEmailTemplatePage(props: PropTypes) {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    register,
    setValue,
    setError,
  } = useForm<UpsertEmailTemplateDto>({
    mode: 'onChange',
    defaultValues: emailTemplateToDto(props.emailTemplate),
  });

  const onSubmit = handleSubmit(
    async (
      data: UpsertEmailTemplateDto,
      _event: React.SyntheticEvent<any, SubmitEvent>,
    ) => {
      setIsLoading(true);

      try {
        await EmailTemplateService.upsert(data);

        window.location.href = REDIRECT_URI;
      } catch (e: unknown) {
        setError('name', {
          type: 'unique',
          message: (e as ApiError).body.message,
        });
      }

      setIsLoading(false);
    },
  );

  const { fields, append, remove } = useFieldArray({
    name: 'attachments',
    control: control,
  });

  const handleRemoveField = (index: number) => {
    const toDelete = fields[index];
    const plainFileToDelete = plainFiles.find(
      (i) => i.name === toDelete.filename,
    );

    remove(index);

    if (plainFileToDelete != null) {
      removeFileByReference(plainFileToDelete);
    }
  };

  const [
    openFileSelector,
    {
      filesContent,
      errors: filePickerErrors,
      plainFiles,
      removeFileByReference,
      clear,
    },
  ] = useImperativeFilePicker({
    multiple: true,
    readAs: 'DataURL',
    maxFileSize: MAX_FILE_SIZE_MB,
    readFilesContent: true,
    limitFilesConfig: {
      max: MAX_FILES,
    },
    validators: [
      new FilesSizeValidator(
        fields.map((v) => v.size),
        MAX_FILES_SIZE_MB,
      ),
      new FilesLimitValidator(fields.length, MAX_FILES),
    ],
  });

  useEffect(() => {
    filesContent.forEach((fileContent: FileContent, index: number) => {
      if (fields.find((i) => i.filename === fileContent.name) == null) {
        append({
          filename: fileContent.name,
          contentBase64: fileContent.content,
          size: plainFiles[index].size,
        });
      }
    });
  }, [filesContent]);

  return (
    <Row className='row g-3 mb-4 align-items-center justify-content-between'>
      <FormErrorMessage error={errors} />
      <Title edit={props.edit} name={props.emailTemplate?.name} />
      <Card>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            <Body
              control={control}
              errors={errors}
              isValid={isValid}
              templateType={props.emailTemplate?.templateType}
              onCancel={() => (window.location.href = REDIRECT_URI)}
              footerFields={fields}
              filePickerErrors={filePickerErrors}
              onSubmit={onSubmit}
              register={register}
              setHtml={(html: string) => setValue('templateHtml', html)}
              setSubjectText={(subject: string) =>
                setValue('subjectText', subject)
              }
              setAttachments={(attachments: AttachmentDto[]) => {
                setValue('attachments', attachments);
                clear();
              }}
              setEmailTemplateReferenceId={(referenceId: number) =>
                setValue('emailTemplateReferenceId', referenceId)
              }
              handleRemoveField={handleRemoveField}
              openFileSelector={openFileSelector}
            />
          </>
        )}
      </Card>
    </Row>
  );
}
